import { Injectable } from '@angular/core';
import {
  toUserProfileRequestDTO,
  toUserOrganismoRequestDTO,
  toUpdatePasswordDTO,
  toActivateAccountDTO,
  toRegisterRequestDTO,
  toVerifyCodeRequestDTO,
  toRegisterOrganismsRequestDTO,
  toDeleteAccountRequestDTO,
  toRegisterPushIdDTO
} from '../mappers/user.mapper';
import { GeneralConstants } from '../constants/general.constants';
import { RestService } from 'src/app/core/services/rest.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { OrganismModel } from '../models/organism.model';
import { IdentityService } from 'src/app/core/services/identity.service';
import { SendSMSRequest } from '../models/request/send-sms.request';
import { BaseRequest } from '../models/request/base.request';
import { BranchOfficeModel } from '../models/branch-office.model';
import { BranchOffice } from '../models/interfaces/branch-office';
import {
  toBranchOfficeModelCollection,
  toBranchOfficeModel,
  toBranchOfficeRequestDTO
} from '../mappers/branch-office.mapper';
import { BranchOfficeForm } from '../models/interfaces/forms/branch-office.form';
import { UserResponsibleForm } from '../models/interfaces/forms/user-responsible.form';
import { PasswordSegurityModel } from '../models/password-segurity.model';
import { Cashier } from '../models/interfaces/cashier';
import { toCashierModelCollection, toCashierRequestDTO, toCashierModel } from '../mappers/cashier.mapper';
import { CashierForm } from '../models/interfaces/forms/cashier.form';
import { AfipModel } from '../models/afip.model';
import { User } from '../models/user';
import { GenericPlace } from '../models/generic-place.model';
import { CuentaVirtualUsuarioModel } from '../models/cuenta-virtual.model';
import { PaymentMethodTypeModel } from '../models/payment-method-type.model';
import { CashierModel } from '../models/cashier.model';
import { UserResponsibleModel } from '../models/user-responsible.model';
import { ActivityModel } from '../models/activity.model';
import { HttpClient, HttpRequest } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { CashierRequestDTO } from '../models/request/cashier-dto.request';
import { UserEditResponsibleRequest, UserResponsibleRequest } from '../models/request/user-responsible.request';
import { NotificationService } from './notification.service';
import { runtimeEnv } from 'src/environments/env-loader';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private result: any;
  showSaldo: boolean = false;
  private PASSWORD_LEVEL_2 = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])');

  constructor(
    private restService: RestService,
    private identityService: IdentityService,
    private storageService: StorageService,
    private http: HttpClient,
    private notificationService: NotificationService
  ) {}

  public sendSMS(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const request = new SendSMSRequest();
      this.restService
        .post(GeneralConstants.sendSMSRoute, this.identityService.getRequest(request))
        .then((response) => resolve(response.object))
        .catch((error) => reject(error.message));
    });
  }

  /**
   * Inicia el registro de un usuario.
   *
   * @param email E-mail del usuario.
   * @param password Contraseña del usuario.
   * @param tel Número telefónico del usuario.
   * @param empresa Empresa proveedora de telefonía del usuario.
   * @returns Promise<UserModel> Modelo del usuario registrado.
   */
  public registerUser(
    email: string,
    password: string,
    tel: string,
    empresa: string,
    recaptchaClientToken: string
  ): Promise<User> {
    return new Promise<User>((resolve, reject) => {
      const request = toRegisterRequestDTO(email, password, tel, empresa, recaptchaClientToken);
      this.restService
        .post(GeneralConstants.registerRoute, this.identityService.getRegistrationRequest(request))
        .then((resp) => resolve(resp.object))
        .catch((error) => reject(error));
    });
  }

  getCurrentUser(): User {
    return this.identityService.getUser();
  }

  public async saveProfile(
    userId: number,
    nombre: string,
    apellido: string,
    nroDoc: string,
    fechaNac: Date
  ): Promise<string> {
    let result: string;
    let request = toUserProfileRequestDTO(userId, nombre, apellido, nroDoc, fechaNac);
    result = await this.restService
      .post(GeneralConstants.saveUserProfile, request)
      .then((response) => {
        return response.message;
      })
      .catch((error: Error) => {
        throw new Error(error.message);
      });
    return result;
  }

  public async savePersonalProfile(
    email: string,
    nroDocumento: string,
    telefono: string,
    cuilCuit: string,
    apellido: string,
    nombres: string,
    fechaNacimiento: string,
    sexo: string,
    nacionalidad: string,
    localidad: string,
    provincia: string,
    calle: string,
    numero: string,
    piso: string,
    dpto: string,
    codigoPostal: string
  ): Promise<string> {
    let result: string;
    let request = {
      email,
      nroDocumento,
      telefono,
      cuilCuit,
      apellido,
      nombres,
      fechaNacimiento,
      sexo,
      nacionalidad,
      localidad,
      provincia,
      calle,
      numero,
      piso,
      dpto,
      codigoPostal
    };
    result = await this.restService
      .put(GeneralConstants.updatePersonaInfo, request)
      .then((response) => {
        return response.message;
      })
      .catch((error: Error) => {
        throw new Error(error.message);
      });
    return result;
  }

  public getPersonaInfo(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.restService
        .get(GeneralConstants.getPersonaInfo, null)
        .then((response) => {
          resolve(response.object);
        })
        .catch((error) => {
          reject(new Error(error.message));
        });
    });
  }

  public async saveOrganismsProfile(
    id: number,
    nombre: string,
    razonSocial: string,
    direccionFiscal: string,
    cuentaBancaria: string,
    numeroCUIT: string,
    condicionIVA: string,
    servicio: string,
    afipData: AfipModel,
    placeLocation: GenericPlace,
    rubro: number
  ): Promise<OrganismModel> {
    let request = toUserOrganismoRequestDTO(
      id,
      nombre,
      razonSocial,
      direccionFiscal,
      cuentaBancaria,
      numeroCUIT,
      condicionIVA,
      servicio,
      rubro
    );
    request.organismoDTO.afipData = afipData;
    request.organismoDTO.ubicacion = placeLocation;
    let endPoint = id ? GeneralConstants.updateOrganismsProfile : GeneralConstants.saveOrganismsProfile;

    try {
      let result = await this.restService.post(endPoint, request);
      if (result) {
        let organism = new OrganismModel();
        organism.id = result.object.id;
        organism.condicionIVA = result.object.condicionIVA;
        organism.nombre = result.object.nombre;
        organism.cbuAlias = result.object.cbuAlias;
        organism.razonSocial = result.object.razonSocial;
        organism.domicilioFiscal = result.object.domicilioFiscal;
        organism.campoServicioEMVco = result.object.campoServicioEMVco;
        organism.numeroDocumento = result.object.numeroDocumento;
        organism.rubroServicio = servicio;
        organism.servicioList = result.object.servicioList;
        organism.ubicacion = result.object.ubicacion;
        organism.rubro = result.object.rubro;

        this.identityService.updateUserOrganism(organism);
        this.identityService.updateUserValidate(true);
        return Promise.resolve(organism);
      }
      return Promise.resolve(null);
    } catch (error) {
      return Promise.reject(new Error(error.message));
    }
  }

  public async updatePassword(token: string, password: string): Promise<string> {
    try {
      const request = toUpdatePasswordDTO(
        token,
        password,
        this.identityService.getUUID(),
        this.identityService.APP_VERSION,
        this.identityService.VERSION
      );
      const response = await this.restService.post(GeneralConstants.updatePassword, request);
      return response.message;
    } catch (error) {
      return Promise.reject(error.message);
    }
  }

  public async activateAccount(token: string): Promise<string> {
    try {
      const request = toActivateAccountDTO(
        token,
        this.identityService.getUUID(),
        this.identityService.APP_VERSION,
        this.identityService.VERSION
      );
      const response = await this.restService.post(GeneralConstants.activateAccount, request);
      return response.message;
    } catch (error) {
      return Promise.reject(error.message);
    }
  }

  public async activateDevice(token: string): Promise<string> {
    try {
      const request = toActivateAccountDTO(
        token,
        this.identityService.getUUID(),
        this.identityService.APP_VERSION,
        this.identityService.VERSION
      );
      const response = await this.restService.post(GeneralConstants.activateDevice, request);
      return response.message;
    } catch (error) {
      return Promise.reject(error.message);
    }
  }

  /**
   * Envia el mail de activacion de cuenta para el usuario actual.
   */
  public sendActivationMail(): void {
    this.restService
      .get(`${GeneralConstants.sendActivationMailRoute}/${this.identityService.getUser().id}`)
      .then((response) => {
        return;
      })
      .catch((error) => console.log(error));
  }

  /**
   * Cancela el registro de usuario borrando el registro en el backend.
   *
   */
  public cancelRegister(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = new BaseRequest();
      this.restService
        .post(GeneralConstants.cancelRegisterRoute, this.identityService.getRequest(request))
        .then((resp) => resolve())
        .catch((error) => reject(error));
    });
  }

  /**
   * Inicia el registro de un comercio.
   *
   * @param email E-mail del usuario.
   * @param password Contraseña del usuario.
   * @param comercioCuit Cuit del comercio.
   * @param comercioRS  razón social del comercio
   * @returns Promise<UserModel> Modelo del usuario registrado.
   */
  public registerOrganisms(
    email: string,
    password: string,
    comercioCuit: string,
    recaptchaClientToken: string
  ): Promise<User> {
    return new Promise<User>((resolve, reject) => {
      const request = toRegisterOrganismsRequestDTO(email, password, comercioCuit, recaptchaClientToken);
      this.restService
        .post(GeneralConstants.registerOrganismsRoute, this.identityService.getRegistrationRequest(request))
        .then((resp) => {
          this.identityService.setUUID(resp.object.deviceUUID);
          resolve(resp.object);
        })
        .catch((error) => reject(error));
    });
  }

  public async getUserCVU(): Promise<CuentaVirtualUsuarioModel> {
    try {
      const response = await this.restService.post(GeneralConstants.userVirtualAccount, {});
      return <CuentaVirtualUsuarioModel>response.object;
    } catch (error) {
      return Promise.reject(error.message);
    }
  }

  public async recoverPassword(email: string): Promise<string> {
    try {
      const request = { email, platform: 9, isPix: true };
      const response = await this.restService.post(GeneralConstants.userPasswordReset, request);
      if (response) {
        return Promise.resolve(response.message);
      }
      return Promise.resolve(null);
    } catch (error) {
      return Promise.reject(new Error(error.message));
    }
  }

  public async getBranchOfficesCollection(): Promise<BranchOfficeModel[]> {
    try {
      const response = await this.restService.get(GeneralConstants.branchOffices, {});
      if (response.listOfResults.size > 0) {
        let collection = <BranchOffice[]>response.listOfResults.listOfResult;
        return Promise.resolve(toBranchOfficeModelCollection(collection));
      }
      return Promise.resolve(null);
    } catch (error) {
      return Promise.reject(new Error(error.message));
    }
  }

  public async getPaymentMethodCollection(): Promise<PaymentMethodTypeModel[]> {
    try {
      const response = await this.restService.post(GeneralConstants.paymentMethodListRoute, {
        filter: { active: true }
      });
      if (response.listOfResults.size > 0) {
        let collection = <PaymentMethodTypeModel[]>response.listOfResults.listOfResult;
        return Promise.resolve(collection);
      }
      return Promise.resolve(null);
    } catch (error) {
      return Promise.reject(new Error(error.message));
    }
  }

  public async saveBranchOffice(request: BranchOfficeForm): Promise<BranchOfficeModel> {
    try {
      const endPoint =
        request.id == undefined ? GeneralConstants.branchOfficesAddRoute : GeneralConstants.branchOfficesUpdateRoute;
      const response = await this.restService.post(endPoint, toBranchOfficeRequestDTO(request));
      if (response.object) {
        return Promise.resolve(toBranchOfficeModel(response.object));
      }
      return Promise.resolve(null);
    } catch (error) {
      return Promise.reject(new Error(error.message));
    }
  }

  public async saveBranchOfficeNew(request: BranchOfficeForm): Promise<BranchOfficeModel> {
    try {
      const sucursalId = request.branchId;

      const endPoint =
        request.id === undefined
          ? GeneralConstants.branchOffices
          : `${GeneralConstants.branchOffices}/${sucursalId ?? ''}`;
      const method = request.id === undefined ? 'POST' : 'PUT';

      let response;
      if (method === 'POST') {
        response = await this.restService.post(endPoint, toBranchOfficeRequestDTO(request));
      } else if (method === 'PUT') {
        response = await this.restService.put(endPoint, toBranchOfficeRequestDTO(request));
      }
      if (response.object) {
        return Promise.resolve(toBranchOfficeModel(response.object));
      }
      return Promise.resolve(null);
    } catch (error) {
      return Promise.reject(new Error(error.message));
    }
  }

  public async saveUserResponsible(request: UserResponsibleForm, state: string): Promise<string> {
    try {
      const req = new UserResponsibleRequest(
        request.nombre,
        request.apellido,
        request.email,
        request.idsSucursal && request.rol === 'RESPONSABLE' ? request.idsSucursal : [],
        request.idResponsable,
        request.rol
      );
      const endPoint =
        state === 'E' ? GeneralConstants.editUserResponsibleRoute : GeneralConstants.addUserResponsibleRoute;
      const response = await this.restService.post(endPoint, req);

      return Promise.resolve(response.message);
    } catch (error) {
      this.notificationService.showError(error.message);

      return Promise.reject(new Error(error.message));
    }
  }

  public async editUserResponsible(request: UserResponsibleForm): Promise<string> {
    try {
      let req = new UserEditResponsibleRequest(
        request.nombre,
        request.apellido,
        request.email,
        request.idsSucursal,
        request.idResponsable,
        request.rol
      );
      const endPoint = GeneralConstants.editUserResponsibleRoute;
      const response = await this.restService.post(endPoint, req);
      return Promise.resolve(response.message);
    } catch (error) {
      return Promise.reject(new Error(error.message));
    }
  }

  public async deleteAccount(password: string): Promise<any> {
    try {
      const response = await this.restService.post(GeneralConstants.deleteAccount, toDeleteAccountRequestDTO(password));
      return response.object;
    } catch (error) {
      return Promise.reject(error.message);
    }
  }

  /* CASHIERS */
  public async getCashiers(): Promise<CashierModel[]> {
    try {
      const response = await this.restService.post(GeneralConstants.cashiersRoute, {});
      if (response.listOfResults.size > 0) {
        let collection = response.listOfResults.listOfResult as Cashier[];
        return Promise.resolve(toCashierModelCollection(collection));
      }
      return [];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  urlAssembler(endPoint: string): string {
    return `${runtimeEnv.APIURL}/${endPoint}`;
  }

  editPuestoCobro(cashierForm: CashierForm, branchId: number) {
    let request = new CashierRequestDTO(cashierForm.name, cashierForm.paymentOrders, cashierForm.externalQrId);
    //api/web/organisms/sucursal/:sucursalId/caja/:cajaId
    return this.http.put(
      `${this.urlAssembler(GeneralConstants.cashierEditRoute)}${branchId}/caja/${cashierForm.id}`,
      request
    );
  }

  public async saveCashier(cashierForm: CashierForm) {
    try {
      const request = new CashierRequestDTO(cashierForm.name, cashierForm.paymentOrders, cashierForm.externalQrId);
      const response = await this.restService.post(
        `${GeneralConstants.cashierAddRoute}${cashierForm.branchOffice.value}/caja`,
        request
      );
      return response;
    } catch (error) {
      return Promise.reject(new Error(error.message));
    }
  }

  getActivityAfip(organisms) {
    if (organisms.actividadAfip !== null) {
      return organisms.actividadAfip;
    } else if (organisms.servicioList) {
      return organisms.servicioList[0].nombre;
    }
  }

  public async getCommerceByUserId(): Promise<OrganismModel> {
    try {
      const response = await this.restService.post(GeneralConstants.getCommerceRoute, {});

      if (response.resultCode != 100 && response.object) {
        let organism = new OrganismModel();
        organism.id = response.object.id;
        organism.condicionIVA = response.object.condicionIVA;
        organism.nombre = response.object.nombre;
        organism.cbuAlias = response.object.cbuAlias;
        organism.razonSocial = response.object.razonSocial;
        organism.domicilioFiscal = response.object.domicilioFiscal;
        organism.campoServicioEMVco = response.object.campoServicioEMVco;
        organism.numeroDocumento = response.object.numeroDocumento;
        organism.servicioList = response.object.servicioList;
        organism.ubicacion = response.object.ubicacion;
        organism.rubro = response.object.rubro;
        organism.actividadAfip = this.getActivityAfip(response.object);
        return Promise.resolve(organism);
      }

      return null;
    } catch (error) {
      return Promise.reject(new Error(error.message));
    }
  }

  public async getTokenCommerce(): Promise<string> {
    try {
      const response = await this.restService.post(GeneralConstants.getTokenRoute, {});
      if (response.object) {
        return Promise.resolve(response.object);
      }
      return null;
    } catch (error) {
      return Promise.reject(new Error(error.message));
    }
  }

  public async getUserResponsible(): Promise<UserResponsibleModel[]> {
    try {
      const response = await this.restService.post(GeneralConstants.getUserResponsibleRoute, {});
      if (response.listOfResults.size > 0) {
        let collection = <UserResponsibleModel[]>response.listOfResults.listOfResult;
        return Promise.resolve(collection);
      }
      return Promise.resolve([]);
    } catch (error) {
      if (error != null) {
        return Promise.resolve(null);
      } else {
        return Promise.reject(new Error(error.message));
      }
    }
  }

  public async removeUserResponsible(email): Promise<string> {
    try {
      const response = await this.restService.delete(GeneralConstants.removeUserResponsibleRoute + `/${email}`);
      return Promise.resolve(response.message);
    } catch (error) {
      Promise.reject(new Error('No se pudo eliminar el usuario, hubo un error interno.'));
    }
  }

  public async activateAccountResponsible(token: string, password: string): Promise<string> {
    try {
      const request = toUpdatePasswordDTO(
        token,
        password,
        this.identityService.getUUID(),
        this.identityService.APP_VERSION,
        this.identityService.VERSION
      );
      const response = await this.restService.post(GeneralConstants.activateAccountResponsible, request);
      return response.message;
    } catch (error) {
      return Promise.reject(error.message);
    }
  }

  /* RAPIPAGO */
  public async getRapipagoTxId(): Promise<string> {
    try {
      const response = await this.restService.get(GeneralConstants.rapipagoTxId);
      return response.object;
    } catch (error) {
      return '';
    }
  }

  public rapipagoCashOut = async () => {
    try {
      const response = await this.restService.get(GeneralConstants.rapipagoCashOut);
      return response.object;
    } catch (e) {
      return '';
    }
  };

  public async registerPushId(pushId: string): Promise<void> {
    try {
      const request = toRegisterPushIdDTO(this.identityService.getUUID(), 1, pushId);
      await this.restService.post(GeneralConstants.registerPushId, request);
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error.message);
    }
  }

  public async getMasterActivities(): Promise<ActivityModel[]> {
    try {
      const response = await this.restService.get(GeneralConstants.getActivities);
      if (response.resultCode != 0) {
        return Promise.reject(new Error(response.message));
      }
      return Promise.resolve(response.listOfResults.listOfResult);
    } catch (error) {
      return Promise.reject(new Error(error.message));
    }
  }

  public async resetDevices(email: string, password: string): Promise<any> {
    try {
      const response = await this.restService.post(GeneralConstants.resetDevice, {
        email,
        password,
        isPix: true,
        platform: 9
      });
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }
}
